/**
 * Update Schedule — step 1. One scroll of everything behind the data date:
 * the Overdue section (record actuals or push dates) and the In-progress
 * section (remaining-duration re-measure). The primary button commits the
 * STATUSING now — staged actuals + pushed dates + duration edits + the
 * in-progress restatement — then opens the New Dates Preview, which
 * previews/commits the downstream reschedule.
 */
import React from "react";
import { apiErrorMessage } from "../../../utils/apiError";
import { formatDateNumeric } from "../../../utils/format";
import { useOverdueReview } from "../review/useOverdueReview";
import ReviewSections from "../review/ReviewSections/ReviewSections";
import "./UpdateScheduleModal.css";

interface Props {
  scheduleId: number;
  onCancel: () => void;
  /** Open the reschedule preview (the statusing is already committed). */
  onProceed: () => void;
  /** Fired after staged updates are committed (refresh diff/changelog). */
  onUpdated?: () => void;
  /** Skip straight to the preview when there's nothing to review. Off after the
   *  user has reached the preview and clicked "← Back" — then the (now-applied)
   *  review is shown with an "up to date" state instead of bouncing back. */
  allowAutoSkip?: boolean;
}

const UpdateScheduleModal: React.FC<Props> = ({
  scheduleId,
  onCancel,
  onProceed,
  onUpdated,
  allowAutoSkip = true,
}) => {
  const r = useOverdueReview(scheduleId);

  // Nothing behind to review (no overdue starts/finishes, no in-progress
  // re-measure). On first open we skip straight to the preview; once the user
  // has been there and come "← Back", we show the review's up-to-date state.
  const allClear = r.loaded && r.overdueCount === 0 && r.autoCount === 0;
  const autoSkip = allowAutoSkip && allClear;
  React.useEffect(() => {
    if (autoSkip) onProceed();
  }, [autoSkip, onProceed]);

  // Whether clicking the primary button will actually commit a statusing batch
  // — drives both the skip and the label so the button can't promise an apply
  // it won't perform.
  const willCommit = r.stagedCount > 0 || r.autoCount > 0 || r.editsMap.size > 0;

  const handleProceed = React.useCallback(async () => {
    // Nothing staged and nothing to re-measure — skip the no-op statusing
    // commit (and the product event it would record) and go straight to the
    // preview.
    if (!willCommit) {
      onProceed();
      return;
    }
    r.setError(null);
    r.setCommitting(true);
    try {
      // Commit the statusing NOW: staged actuals + pushed dates + duration
      // edits + the in-progress re-measure. The preview that opens next only
      // previews/commits the downstream reschedule.
      await r.applyStatusing();
    } catch (e) {
      r.setError(apiErrorMessage(e, "Failed to apply updates"));
      return;
    } finally {
      r.setCommitting(false);
    }
    onUpdated?.();
    onProceed();
  }, [r, willCommit, onProceed, onUpdated]);

  // Auto-skipping straight to the preview — don't flash the empty review modal.
  if (autoSkip) return null;

  const label = r.committing
    ? "Applying…"
    : !willCommit
      ? "Review new dates →"
      : r.stagedCount > 0
        ? `Apply ${r.stagedCount} update${r.stagedCount > 1 ? "s" : ""} & review new dates →`
        : "Apply all & review new dates →";

  return (
    <div className="f9-overlay" onClick={onCancel}>
      <div className="f9-modal" onClick={(e) => e.stopPropagation()}>
        <header className="f9-header">
          <div className="f9-title-row">
            <div className="f9-title">Update Schedule</div>
            {r.review?.data_date && (
              <span className="f9-datadate">data date {formatDateNumeric(r.review.data_date)}</span>
            )}
          </div>
        </header>

        {allClear ? (
          <div className="f9-allclear">
            <span className="f9-allclear-check">✓</span>
            <div className="f9-allclear-text">
              <div className="f9-allclear-title">Up to date</div>
              <div className="f9-allclear-sub">
                All overdue and in-progress work is applied — nothing left to update.
              </div>
            </div>
          </div>
        ) : (
          <ReviewSections r={r} />
        )}

        <footer className="f9-footer">
          <span className="f9-legend">
            {allClear
              ? "Nothing to apply — continue to the new-dates preview."
              : "Records what happened + your pushed dates, then previews the reschedule."}
          </span>
          <button className="f9-btn" onClick={onCancel} disabled={r.committing}>Close</button>
          <button
            className="f9-btn f9-btn--primary"
            onClick={handleProceed}
            disabled={!r.loaded || r.committing}
          >
            {label}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default UpdateScheduleModal;
